import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAdvertiser } from "../contexts/AdvertiserContext";

const safeBase = (base) => String(base || "").trim().replace(/\/+$/, "");
const normalizeId = (value) =>
  value == null || !String(value).trim() ? "" : String(value).trim();
const normalizeName = (value) => String(value || "").trim();

export const coerceGovernedAdvertiserArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.advertisers)) return payload.advertisers;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

export const normalizeGovernedAdvertisers = (payload) =>
  coerceGovernedAdvertiserArray(payload)
    .map((raw) => {
      const idRaw =
        raw?.id ??
        raw?._id ??
        raw?.advertiserId ??
        raw?.advertiser_id ??
        null;
      const id = normalizeId(idRaw);
      const name = normalizeName(
        raw?.name ??
          raw?.advertiser ??
          raw?.advertiser_name ??
          raw?.title ??
          raw?.company ??
          raw?.businessName ??
          raw?.displayName
      );
      if (!id || !name) return null;
      return {
        id: typeof idRaw === "number" ? idRaw : id,
        name,
        advertiser: name,
        vertical: raw?.vertical || "",
        raw,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.name.localeCompare(right.name));

export const resolveGovernedVisibleAdvertisers = ({
  advertisers = [],
  accessIds = [],
  role = "client",
  catalogConfirmed = false,
  accessConfirmed = false,
} = {}) => {
  if (!catalogConfirmed) return [];
  const list = Array.isArray(advertisers) ? advertisers : [];
  if (String(role).toLowerCase() === "superadmin") return list;
  if (!accessConfirmed) return [];
  const allowed = new Set(
    (Array.isArray(accessIds) ? accessIds : [])
      .map(normalizeId)
      .filter(Boolean)
  );
  return list.filter((advertiser) => allowed.has(normalizeId(advertiser?.id)));
};

export const resolveGovernedCurrentAdvertiser = ({
  visibleAdvertisers = [],
  selectedAdvertiserId = null,
  selectedAdvertiserName = "",
} = {}) => {
  const selectedId = normalizeId(selectedAdvertiserId);
  if (selectedId) {
    return (
      visibleAdvertisers.find(
        (advertiser) => normalizeId(advertiser?.id) === selectedId
      ) || null
    );
  }
  const selectedName = normalizeName(selectedAdvertiserName);
  if (!selectedName) return null;
  return (
    visibleAdvertisers.find(
      (advertiser) => normalizeName(advertiser?.name) === selectedName
    ) || null
  );
};

export const resolveGovernedAdvertiserSelection = ({
  advertiser = null,
  visibleAdvertisers = [],
  selectedAdvertiserId = null,
  selectedAdvertiserName = "",
} = {}) => {
  const requestedId = normalizeId(advertiser?.id);
  const requestedName = normalizeName(
    advertiser?.name || advertiser?.advertiser
  );
  const authorizedAdvertiser =
    (requestedId &&
      visibleAdvertisers.find(
        (candidate) => normalizeId(candidate?.id) === requestedId
      )) ||
    (!requestedId &&
      requestedName &&
      visibleAdvertisers.find(
        (candidate) => normalizeName(candidate?.name) === requestedName
      )) ||
    null;
  if (!authorizedAdvertiser) {
    return { status: "unauthorized", advertiser: null };
  }

  const currentId = normalizeId(selectedAdvertiserId);
  const currentName = normalizeName(selectedAdvertiserName);
  const current =
    (currentId &&
      normalizeId(authorizedAdvertiser.id) === currentId) ||
    (!currentId &&
      currentName &&
      normalizeName(authorizedAdvertiser.name) === currentName);
  return {
    status: current ? "current" : "changed",
    advertiser: authorizedAdvertiser,
  };
};

// A failed catalog fetch and an expired session are different problems with
// different recovery actions. Collapsing both into "unable to confirm the
// governed advertiser catalog" tells an operator nothing about what to do, so
// each failure mode carries its own governed state.
export const GOVERNED_CATALOG_ERROR_STATE = Object.freeze({
  NONE: "none",
  NOT_AUTHENTICATED: "not_authenticated",
  SESSION_EXPIRED: "session_expired",
  TEMPORARILY_UNAVAILABLE: "temporarily_unavailable",
  CATALOG_UNAVAILABLE: "catalog_unavailable",
});

const GOVERNED_CATALOG_ERROR_MESSAGE = Object.freeze({
  [GOVERNED_CATALOG_ERROR_STATE.NONE]: "",
  [GOVERNED_CATALOG_ERROR_STATE.NOT_AUTHENTICATED]:
    "SIGN IN AGAIN TO CONFIRM THE GOVERNED ADVERTISER CATALOG.",
  [GOVERNED_CATALOG_ERROR_STATE.SESSION_EXPIRED]:
    "YOUR SESSION EXPIRED. SIGN IN AGAIN TO RESTORE THE GOVERNED ADVERTISER CATALOG.",
  [GOVERNED_CATALOG_ERROR_STATE.TEMPORARILY_UNAVAILABLE]:
    "THE GOVERNED ADVERTISER CATALOG IS TEMPORARILY UNAVAILABLE. RETRY IN A MOMENT.",
  [GOVERNED_CATALOG_ERROR_STATE.CATALOG_UNAVAILABLE]:
    "UNABLE TO CONFIRM THE GOVERNED ADVERTISER CATALOG.",
});

export const classifyGovernedCatalogError = (code) => {
  const raw = String(code || "").trim();
  if (!raw) return GOVERNED_CATALOG_ERROR_STATE.NONE;
  if (/_unavailable$/.test(raw)) {
    return GOVERNED_CATALOG_ERROR_STATE.NOT_AUTHENTICATED;
  }
  const status = Number(raw.replace(/^.*_(\d{3})$/, "$1"));
  if (status === 401 || status === 403) {
    return GOVERNED_CATALOG_ERROR_STATE.SESSION_EXPIRED;
  }
  if (status === 429 || status === 502 || status === 503 || status === 504) {
    return GOVERNED_CATALOG_ERROR_STATE.TEMPORARILY_UNAVAILABLE;
  }
  return GOVERNED_CATALOG_ERROR_STATE.CATALOG_UNAVAILABLE;
};

export const governedCatalogErrorMessage = (state) =>
  GOVERNED_CATALOG_ERROR_MESSAGE[state] ||
  GOVERNED_CATALOG_ERROR_MESSAGE[GOVERNED_CATALOG_ERROR_STATE.CATALOG_UNAVAILABLE];

const getStoredValue = (keys) => {
  for (const key of keys) {
    const value = localStorage.getItem(key);
    if (value && String(value).trim()) return String(value).trim();
  }
  return "";
};

const getAuthToken = (role) =>
  getStoredValue(
    String(role).toLowerCase() === "superadmin"
      ? [
          "superadminToken",
          "authToken",
          "token",
          "accessToken",
        ]
      : ["clientAuthToken", "authToken", "token", "accessToken"]
  );

const getUserEmail = () => getStoredValue(["userEmail", "email"]);

const extractAccessIds = (payload) => {
  const source =
    (Array.isArray(payload?.advertisers) && payload.advertisers.length
      ? payload.advertisers
      : payload?.advertiser_ids) || [];
  return (Array.isArray(source) ? source : [])
    .map((entry) =>
      normalizeId(
        typeof entry === "object"
          ? entry?.id ?? entry?.advertiserId ?? entry?.advertiser_id
          : entry
      )
    )
    .filter(Boolean);
};

const isAbortError = (error) =>
  error?.name === "AbortError" || error?.code === "ABORT_ERR";

export default function useGovernedAdvertiserCatalog({
  apiBase = "",
  role = "client",
  onAdvertiserChange = null,
} = {}) {
  const {
    selectedAdvertiserId = null,
    selectedAdvertiserName = "",
    selectAdvertiser,
  } = useAdvertiser() || {};
  const normalizedRole =
    String(role).toLowerCase() === "superadmin" ? "superadmin" : "client";
  const [advertisers, setAdvertisers] = useState([]);
  const [accessIds, setAccessIds] = useState([]);
  const [catalogReady, setCatalogReady] = useState(false);
  const [accessReady, setAccessReady] = useState(false);
  const [catalogConfirmed, setCatalogConfirmed] = useState(false);
  const [accessConfirmed, setAccessConfirmed] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [accessError, setAccessError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const mountedRef = useRef(true);
  const catalogControllerRef = useRef(null);
  const accessControllerRef = useRef(null);
  const catalogRequestRef = useRef(0);
  const accessRequestRef = useRef(0);
  const onAdvertiserChangeRef = useRef(onAdvertiserChange);
  const clearedSelectionRef = useRef("");
  const selectorOpeningRef = useRef(false);
  const selectorModalOpenRef = useRef(false);
  const documentWasAwayRef = useRef(false);

  useEffect(() => {
    onAdvertiserChangeRef.current = onAdvertiserChange;
  }, [onAdvertiserChange]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refreshCatalog = useCallback(async () => {
    catalogControllerRef.current?.abort();
    const controller = new AbortController();
    catalogControllerRef.current = controller;
    const requestId = ++catalogRequestRef.current;
    const token = getAuthToken(normalizedRole);
    const base = safeBase(apiBase);

    setCatalogLoading(true);
    setCatalogError("");
    if (!base || !token) {
      if (mountedRef.current && requestId === catalogRequestRef.current) {
        setAdvertisers([]);
        setCatalogConfirmed(false);
        setCatalogReady(true);
        setCatalogLoading(false);
        setCatalogError("advertiser_catalog_unavailable");
      }
      return { confirmed: false, advertisers: [] };
    }

    try {
      const params = new URLSearchParams();
      const email = getUserEmail();
      if (normalizedRole === "client" && email) params.set("email", email);
      params.set("ts", Date.now().toString());
      const response = await fetch(
        `${base}/api/advertisers?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
          credentials: "include",
          signal: controller.signal,
        }
      );
      if (!response.ok) {
        throw new Error(`advertiser_catalog_${response.status}`);
      }
      const list = normalizeGovernedAdvertisers(await response.json());
      if (
        !mountedRef.current ||
        controller.signal.aborted ||
        requestId !== catalogRequestRef.current
      ) {
        return { confirmed: false, advertisers: [], stale: true };
      }
      setAdvertisers(list);
      setCatalogConfirmed(true);
      setCatalogReady(true);
      return { confirmed: true, advertisers: list };
    } catch (error) {
      if (isAbortError(error) || controller.signal.aborted) {
        return { confirmed: false, advertisers: [], aborted: true };
      }
      if (mountedRef.current && requestId === catalogRequestRef.current) {
        setAdvertisers([]);
        setCatalogConfirmed(false);
        setCatalogError(error?.message || "advertiser_catalog_unavailable");
        setCatalogReady(true);
      }
      return { confirmed: false, advertisers: [], error };
    } finally {
      if (mountedRef.current && requestId === catalogRequestRef.current) {
        setCatalogLoading(false);
      }
    }
  }, [apiBase, normalizedRole]);

  const refreshAccess = useCallback(async () => {
    accessControllerRef.current?.abort();
    const controller = new AbortController();
    accessControllerRef.current = controller;
    const requestId = ++accessRequestRef.current;
    const token = getAuthToken(normalizedRole);
    const base = safeBase(apiBase);

    setAccessLoading(true);
    setAccessError("");
    if (!base || !token) {
      if (mountedRef.current && requestId === accessRequestRef.current) {
        setAccessIds([]);
        setAccessConfirmed(false);
        setAccessReady(true);
        setAccessLoading(false);
        setAccessError("advertiser_access_unavailable");
      }
      return { confirmed: false, accessIds: [] };
    }

    try {
      const response = await fetch(`${base}/api/auth/me?ts=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
        credentials: "include",
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`advertiser_access_${response.status}`);
      const ids = extractAccessIds(await response.json());
      if (
        !mountedRef.current ||
        controller.signal.aborted ||
        requestId !== accessRequestRef.current
      ) {
        return { confirmed: false, accessIds: [], stale: true };
      }
      setAccessIds(ids);
      setAccessConfirmed(true);
      setAccessReady(true);
      return { confirmed: true, accessIds: ids };
    } catch (error) {
      if (isAbortError(error) || controller.signal.aborted) {
        return { confirmed: false, accessIds: [], aborted: true };
      }
      if (mountedRef.current && requestId === accessRequestRef.current) {
        setAccessIds([]);
        setAccessConfirmed(false);
        setAccessError(error?.message || "advertiser_access_unavailable");
        setAccessReady(true);
      }
      return { confirmed: false, accessIds: [], error };
    } finally {
      if (mountedRef.current && requestId === accessRequestRef.current) {
        setAccessLoading(false);
      }
    }
  }, [apiBase, normalizedRole]);

  useEffect(() => {
    refreshCatalog();
    refreshAccess();
    const handleBlur = () => {
      documentWasAwayRef.current = true;
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        documentWasAwayRef.current = true;
      }
    };
    const handleFocus = (event) => {
      // Element focus can reach the window listener in browsers. Revalidating
      // there would disable the just-restored trigger and move focus to body.
      // Only an actual browser-window focus event should refresh authority.
      if (event?.target && event.target !== window && event.target !== document) {
        return;
      }
      // Opening the governed selector already performs a fresh access check.
      // A focus event from that same interaction must not supersede and abort
      // the access request that openSelector is awaiting.
      if (selectorOpeningRef.current) return;
      // Autofocusing the first modal option can activate a previously
      // unfocused browser target before the element-focus event arrives. That
      // is not a return from background and must not disable the trigger just
      // before focus restoration. A real blur/hidden transition still marks
      // the document away and therefore revalidates while the modal is open.
      if (
        selectorModalOpenRef.current &&
        !documentWasAwayRef.current
      ) {
        return;
      }
      documentWasAwayRef.current = false;
      refreshAccess();
    };
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      catalogControllerRef.current?.abort();
      accessControllerRef.current?.abort();
    };
  }, [refreshAccess, refreshCatalog]);

  const visibleAdvertisers = useMemo(
    () =>
      resolveGovernedVisibleAdvertisers({
        advertisers,
        accessIds,
        role: normalizedRole,
        catalogConfirmed,
        accessConfirmed,
      }),
    [
      accessConfirmed,
      accessIds,
      advertisers,
      catalogConfirmed,
      normalizedRole,
    ]
  );

  const currentAdvertiser = useMemo(
    () =>
      resolveGovernedCurrentAdvertiser({
        visibleAdvertisers,
        selectedAdvertiserId,
        selectedAdvertiserName,
      }),
    [selectedAdvertiserId, selectedAdvertiserName, visibleAdvertisers]
  );

  const authorityConfirmed =
    catalogConfirmed &&
    (normalizedRole === "superadmin" || accessConfirmed);
  const selectionKey = `${normalizeId(selectedAdvertiserId)}:${normalizeName(
    selectedAdvertiserName
  )}`;

  useEffect(() => {
    if (!authorityConfirmed || selectionKey === ":") return;
    if (currentAdvertiser) {
      clearedSelectionRef.current = "";
      return;
    }
    if (clearedSelectionRef.current === selectionKey) return;
    clearedSelectionRef.current = selectionKey;
    selectorModalOpenRef.current = false;
    setModalOpen(false);
    onAdvertiserChangeRef.current?.(null, {
      reason: "confirmed_removed",
    });
    selectAdvertiser?.(null);
  }, [
    authorityConfirmed,
    currentAdvertiser,
    selectAdvertiser,
    selectionKey,
  ]);

  const closeSelector = useCallback(() => {
    selectorModalOpenRef.current = false;
    setModalOpen(false);
  }, []);

  const openSelector = useCallback(async () => {
    if (selectorOpeningRef.current) return false;
    if (!authorityConfirmed || !visibleAdvertisers.length) return false;
    selectorOpeningRef.current = true;
    try {
      const [refreshedCatalog, refreshedAccess] = await Promise.all([
        refreshCatalog(),
        refreshAccess(),
      ]);
      if (
        !refreshedCatalog.confirmed ||
        (normalizedRole === "client" && !refreshedAccess.confirmed)
      ) {
        selectorModalOpenRef.current = false;
        setModalOpen(false);
        return false;
      }
      const selectableAdvertisers = resolveGovernedVisibleAdvertisers({
        advertisers: refreshedCatalog.advertisers,
        accessIds: refreshedAccess.accessIds,
        role: normalizedRole,
        catalogConfirmed: true,
        accessConfirmed: refreshedAccess.confirmed,
      });
      if (!selectableAdvertisers.length) {
        selectorModalOpenRef.current = false;
        setModalOpen(false);
        return false;
      }
      if (!mountedRef.current) return false;
      selectorModalOpenRef.current = true;
      setModalOpen(true);
      return true;
    } finally {
      selectorOpeningRef.current = false;
    }
  }, [
    authorityConfirmed,
    normalizedRole,
    refreshAccess,
    refreshCatalog,
    visibleAdvertisers.length,
  ]);

  const handleAdvertiserSelect = useCallback(
    (advertiser) => {
      const resolution = resolveGovernedAdvertiserSelection({
        advertiser,
        visibleAdvertisers,
        selectedAdvertiserId,
        selectedAdvertiserName,
      });
      selectorModalOpenRef.current = false;
      setModalOpen(false);
      if (
        resolution.status !== "changed" ||
        !resolution.advertiser
      ) {
        return resolution.status;
      }
      onAdvertiserChangeRef.current?.(resolution.advertiser, {
        reason: "changed",
      });
      selectAdvertiser?.(resolution.advertiser);
      return "changed";
    },
    [
      selectAdvertiser,
      selectedAdvertiserId,
      selectedAdvertiserName,
      visibleAdvertisers,
    ]
  );

  const activeError =
    catalogError || (normalizedRole === "client" ? accessError : "");
  const errorState = classifyGovernedCatalogError(activeError);

  const loading =
    catalogLoading ||
    !catalogReady ||
    (normalizedRole === "client" && (accessLoading || !accessReady));
  const selectorDisabled = !authorityConfirmed || loading;
  const selectorEmpty =
    authorityConfirmed && visibleAdvertisers.length === 0;

  return {
    selectedAdvertiserId,
    selectedAdvertiserName,
    visibleAdvertisers,
    currentAdvertiser,
    catalogReady,
    accessReady,
    catalogConfirmed,
    accessConfirmed,
    loading,
    error: activeError,
    errorState,
    errorMessage: activeError ? governedCatalogErrorMessage(errorState) : "",
    sessionExpired: errorState === GOVERNED_CATALOG_ERROR_STATE.SESSION_EXPIRED,
    retryable:
      errorState === GOVERNED_CATALOG_ERROR_STATE.TEMPORARILY_UNAVAILABLE ||
      errorState === GOVERNED_CATALOG_ERROR_STATE.CATALOG_UNAVAILABLE,
    selectorDisabled,
    selectorEmpty,
    modalOpen,
    openSelector,
    closeSelector,
    handleAdvertiserSelect,
    refreshCatalog,
    refreshAccess,
  };
}
